<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>Dynamiczne ceny</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li class="active">
          <strong>Dynamiczne ceny</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Konfiguracja</h5>
          </div>
          <div class="ibox-content">
            <div v-if="settingsErrorMessage" class="alert alert-danger">
              {{ settingsErrorMessage }}
            </div>
            <div class="form-horizontal">
              <div class="ibox-content">
                <div class="form-group">
                  <label class="control-label col-sm-2">Włączone</label>
                  <div class="col-sm-10">
                    <input
                      type="checkbox"
                      v-model="form.active"
                      class="m-t-xs"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Pożądany średni obrót miesięczny
                  </label>
                  <div class="col-sm-10">
                    <input
                      v-model.number="form.amount_avg"
                      type="number"
                      step="0.01"
                      class="form-control inputClass"
                      placeholder="0.00"
                    />
                    <div v-if="fieldErrors.amount_avg" class="has-error">
                      <span class="help-block">
                        {{ fieldErrors.amount_avg }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Minimalny procent rabatu
                  </label>
                  <div class="col-sm-10">
                    <input
                      v-model.number="form.min_percent"
                      type="number"
                      step="0.01"
                      class="form-control inputClass"
                      placeholder="0.00"
                    />
                    <div v-if="fieldErrors.min_percent" class="has-error">
                      <span class="help-block">
                        {{ fieldErrors.min_percent }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Maksymalny procent rabatu
                  </label>
                  <div class="col-sm-10">
                    <input
                      v-model.number="form.max_percent"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      class="form-control inputClass"
                      placeholder="0.00"
                    />
                    <div v-if="fieldErrors.max_percent" class="has-error">
                      <span class="help-block">
                        {{ fieldErrors.max_percent }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Modyfikator rabatu (%)
                  </label>
                  <div class="col-sm-10">
                    <input
                      v-model.number="form.modifier"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      class="form-control inputClass"
                      placeholder="0.00"
                    />
                    <div v-if="fieldErrors.modifier" class="has-error">
                      <span class="help-block">
                        {{ fieldErrors.modifier }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="hr-line-dashed"></div>

                <div class="form-group">
                  <div class="col-sm-4 col-sm-offset-2">
                    <button class="btn btn-white" @click="resetForm">
                      Anuluj
                    </button>
                    <button
                      v-if="canChangeSettings"
                      @click="save"
                      class="btn btn-primary"
                      :disabled="saving"
                    >
                      Zapisz
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Podsumowanie obrotów</h5>
          </div>
          <div class="ibox-content">
            <div v-if="summaryErrorMessage" class="alert alert-warning">
              {{ summaryErrorMessage }}
            </div>
            <div class="row">
              <div class="col-sm-4">
                <div>
                  <strong>Suma obrotu:</strong> {{ summary.total_turnover }}
                </div>
                <div>
                  <strong>Średni obrót:</strong> {{ summary.average_turnover }}
                </div>
                <div>
                  <strong>Rabat (bez modyfikacji):</strong>
                  {{ summary.discount_amount }}%
                </div>
                <div>
                  <strong>Rabat (po modyfikacji):</strong>
                  {{ summary.modified_discount_amount }}%
                </div>
              </div>
              <div class="col-sm-8">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>Tydzień</th>
                        <th>Od</th>
                        <th>Do</th>
                        <th>Średni obrót</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="w in summary.weekly" :key="w.week_offset">
                        <td>{{ w.week_offset }}</td>
                        <td>{{ w.start_date }}</td>
                        <td>{{ w.end_date }}</td>
                        <td>{{ w.turnover }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import { turnoverDiscountSettingPermissionsConst } from "../../constants/productConstants";

export default {
  name: "DynamicPrices",
  setup() {
    const store = useStore();
    const canChangeSettings = computed(() =>
      hasPermission(turnoverDiscountSettingPermissionsConst.update)
    );
    const form = ref({
      active: false,
      amount_avg: 0.0,
      min_percent: 0.0,
      max_percent: 0.0,
      modifier: 0.0,
    });
    const summary = ref({
      weekly: [],
      total_turnover: 0.0,
      average_turnover: 0.0,
      discount_amount: 0.0,
      modified_discount_amount: 0.0,
    });
    const fieldErrors = ref({});
    const settingsErrorMessage = ref("");
    const summaryErrorMessage = ref("");
    const saving = ref(false);

    const settingsData = computed(
      () => store.getters.getTurnoverDiscountSettings
    );
    const saveData = computed(
      () => store.getters.getTurnoverDiscountSettingsSave
    );
    const summaryData = computed(
      () => store.getters.getTurnoverDiscountSummary
    );

    const load = async () => {
      settingsErrorMessage.value = "";
      summaryErrorMessage.value = "";
      await store.dispatch("fetchTurnoverDiscountSettings");
      await store.dispatch("fetchTurnoverDiscountSummary");
    };

    const resetForm = () => {
      if (settingsData.value?.data) {
        form.value = { ...settingsData.value.data };
      }
    };

    const save = async () => {
      if (!canChangeSettings.value) {
        return;
      }
      fieldErrors.value = {};
      settingsErrorMessage.value = "";
      saving.value = true;
      await store.dispatch("saveTurnoverDiscountSettings", form.value);
      saving.value = false;
    };

    onMounted(() => {
      load();
    });

    watch(
      () => settingsData.value,
      (newValue) => {
        if (newValue?.data) {
          if (newValue.status && newValue.status !== 200) {
            settingsErrorMessage.value =
              newValue.data?.detail || "Nie udało się pobrać ustawień.";
            return;
          }
          form.value = { ...newValue.data };
        }
      }
    );

    watch(
      () => summaryData.value,
      (newValue) => {
        if (newValue?.data) {
          if (newValue.status && newValue.status !== 200) {
            summaryErrorMessage.value =
              newValue.data?.detail || "Nie udało się pobrać podsumowania.";
            return;
          }
          summary.value = newValue.data;
        }
      }
    );

    watch(
      () => saveData.value,
      (newValue) => {
        if (newValue?.status === 200) {
          store.dispatch("showToaster", {
            type: "success",
            content: "Zapisano konfigurację dynamicznych cen",
          });
          // Odswież podsumowanie po zapisie (np. po zmianie modyfikatora)
          store.dispatch("fetchTurnoverDiscountSummary");
          fieldErrors.value = {};
        } else if (newValue) {
          const errors = {};
          if (newValue.data && typeof newValue.data === "object") {
            [
              "active",
              "amount_avg",
              "min_percent",
              "max_percent",
              "modifier",
            ].forEach((key) => {
              if (newValue.data[key]) {
                errors[key] = Array.isArray(newValue.data[key])
                  ? newValue.data[key][0]
                  : newValue.data[key];
              }
            });
          }
          fieldErrors.value = errors;
          store.dispatch("showToaster", {
            type: "error",
            content:
              Object.values(errors)[0] ||
              newValue.data?.detail ||
              "Nie udało się zapisać ustawień.",
          });
        }
      }
    );

    return {
      canChangeSettings,
      form,
      summary,
      save,
      resetForm,
      fieldErrors,
      settingsErrorMessage,
      summaryErrorMessage,
      saving,
    };
  },
};
</script>
